import SimulateService from '../../../../service/SimulateService'
import { SCENARIO_STATUS } from '../../constants'
import { ScenarioResponseType } from '../../types'

const QUICK_DEPLOY_POLL_INTERVAL = 5000
const QUICK_DEPLOY_MAX_POLL_ATTEMPTS = 60
const QUICK_DEPLOY_MAX_MISSING_SCENARIO_ATTEMPTS = 3
export const QUICK_DEPLOY_RETURN_TO_LIST_ERROR = 'QUICK_DEPLOY_RETURN_TO_LIST'

type PollQuickDeployScenarioParams = {
    missionId: string;
    scenarioTitle: string;
    initialScenario?: ScenarioResponseType | null;
}

const getMatchingQuickDeployScenario = (
    scenarioList: ScenarioResponseType[],
    scenarioTitle: string,
    initialScenario?: ScenarioResponseType | null
) => {
    // Prefer backend-returned ids when available; this keeps the old deploy flow tied to the exact scenario.
    const initialScenarioId = initialScenario?.scenarioId || initialScenario?.uniqueId

    if (initialScenarioId) {
        const matchedById = scenarioList.find((item) => [item?.scenarioId, item?.uniqueId].includes(initialScenarioId))
        return matchedById
    }

    // If backend does not return ids, fall back to the latest matching title.
    return scenarioList
        .filter((item) => item?.scenarioName === scenarioTitle)
        .sort((a, b) => Date.parse(b?.createdAt) - Date.parse(a?.createdAt))?.[0]
}

export const createQuickDeployScenarioPoller = () => {
    let timeout: ReturnType<typeof setTimeout> | null = null
    let resolveWait: (() => void) | null = null
    let pollId = 0

    // Keep the delay cancellable so closing the modal does not leave a pending poll behind.
    const waitForNextPoll = () => new Promise<void>((resolve) => {
        resolveWait = resolve
        timeout = setTimeout(() => {
            timeout = null
            resolveWait = null
            resolve()
        }, QUICK_DEPLOY_POLL_INTERVAL)
    })

    // Invalidates the current poll loop and resolves any pending delay immediately.
    const cancel = () => {
        pollId += 1
        if (timeout) {
            clearTimeout(timeout)
            timeout = null
        }
        resolveWait?.()
        resolveWait = null
    }

    const throwReturnToListError = () => {
        const error = new Error('Deployment failed')
        error.name = QUICK_DEPLOY_RETURN_TO_LIST_ERROR
        throw error
    }

    const poll = async ({ missionId, scenarioTitle, initialScenario }: PollQuickDeployScenarioParams) => {
        // Backend now accepts deployment asynchronously; poll scenario list until it reaches the old ready state.
        const currentPollId = pollId
        let missingScenarioAttempts = 0

        for (let attempt = 0; attempt < QUICK_DEPLOY_MAX_POLL_ATTEMPTS; attempt += 1) {
            if (currentPollId !== pollId) return null

            const response = await SimulateService.getALLScenarios(missionId)
            const payload = response?.data?.data ?? response?.data ?? []
            const scenarioList = Array.isArray(payload) ? payload : []
            const latestScenario = getMatchingQuickDeployScenario(scenarioList, scenarioTitle, initialScenario)
            const latestStatus = latestScenario?.status

            // Failed quick deploys can be auto-deleted from the list; after a short grace period, return to list.
            if (!latestScenario) {
                missingScenarioAttempts += 1
                if (missingScenarioAttempts >= QUICK_DEPLOY_MAX_MISSING_SCENARIO_ATTEMPTS) {
                    throwReturnToListError()
                }
                await waitForNextPoll()
                continue
            }
            missingScenarioAttempts = 0

            // If backend returns the scenario without a status, stop polling and send the user back to the list.
            if (latestScenario && !latestStatus) {
                throwReturnToListError()
            }

            if (latestStatus === SCENARIO_STATUS.SIMULATION_RUNNING) {
                return latestScenario
            }

            if (String(latestStatus || '').toLowerCase().includes('failed')) {
                throw new Error(latestScenario?.setupStatusMessage || 'Something went wrong, deployment failed')
            }

            await waitForNextPoll()
        }

        throw new Error('Deployment is taking longer than expected. Please check scenario list.')
    }

    return { poll, cancel }
}
